'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { api } from '@/lib/api';
import { useAuth } from './auth-context';
import { logError } from '@/lib/utils';
import { buildRouteWithAccount } from '@/lib/routes';

/**
 * Gmail account information.
 */
export interface GmailAccount {
  id: string;
  email: string;
  is_primary: boolean;
  credentials_invalid?: boolean;
  created_at: string;
}

/**
 * Gmail account context type.
 */
interface GmailAccountContextType {
  gmailAccounts: GmailAccount[];
  selectedGmailAccountId: string | null;
  setSelectedGmailAccountId: (accountId: string | null) => void;
  loading: boolean;
  refreshAccounts: () => Promise<void>;
}

const GmailAccountContext = createContext<GmailAccountContextType | undefined>(undefined);

/**
 * Provider component for Gmail account management.
 *
 * Manages Gmail account state, URL synchronization, and localStorage persistence.
 * The selected account ID is stored in the URL query parameter `gmail_account_id`
 * to support refresh and direct links.
 *
 * @param children - React children to wrap with the provider
 *
 * @example
 * ```tsx
 * <GmailAccountProvider>
 *   <App />
 * </GmailAccountProvider>
 * ```
 *
 * @see {@link useGmailAccount} - Hook to access Gmail account context
 */
export function GmailAccountProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [gmailAccounts, setGmailAccounts] = useState<GmailAccount[]>([]);
  const [selectedGmailAccountId, setSelectedGmailAccountId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Track the source of account changes to prevent URL sync loops
  // 'url' = change came from URL params, 'user' = manual selection, 'auto' = default selection
  const changeSourceRef = useRef<'url' | 'user' | 'auto' | null>(null);

  // Track if we're in the settling period after accounts load
  const isSettlingRef = useRef(true);

  // Track the debounced URL update timeout
  const urlUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track if accounts have been loaded at least once
  const hasLoadedAccountsRef = useRef(false);

  /**
   * Select the default account (primary or first account).
   * Used when no account is selected and no account ID is in URL or storage.
   *
   * @param accounts - Array of Gmail accounts
   * @returns Account ID of primary account, or first account if no primary exists, or null if no accounts
   */
  const selectDefaultAccount = useCallback((accounts: GmailAccount[]): string | null => {
    if (accounts.length === 0) return null;
    const primaryAccount = accounts.find((acc) => acc.is_primary) || accounts[0];
    return primaryAccount.id;
  }, []);

  /**
   * Fetch and refresh Gmail accounts from the API.
   * Automatically selects the primary or first account if no account is currently selected.
   *
   * @returns Promise that resolves when accounts are fetched
   */
  const refreshAccounts = useCallback(async () => {
    if (!user) {
      setGmailAccounts([]);
      setSelectedGmailAccountId(null);
      setLoading(false);
      return;
    }

    try {
      const response = await api.getGmailAccounts();
      const accounts: GmailAccount[] = (response.accounts || []).map((acc) => ({
        id: acc.id,
        email: acc.email,
        is_primary: acc.is_primary,
        credentials_invalid: acc.credentials_invalid || false,
        created_at: acc.created_at,
      }));
      setGmailAccounts(accounts);

      // If no account is selected, select primary or first account
      if (accounts.length > 0) {
        setSelectedGmailAccountId((currentSelected) => {
          const accountExists = accounts.some((acc) => acc.id === currentSelected);

          if (!currentSelected || !accountExists) {
            return selectDefaultAccount(accounts);
          }
          return currentSelected;
        });
      } else {
        setSelectedGmailAccountId(null);
      }
    } catch (error) {
      logError(
        'Failed to fetch Gmail accounts',
        {
          userId: user?.id,
          error: error instanceof Error ? error.message : String(error),
        },
        error instanceof Error ? error : undefined
      );
      setGmailAccounts([]);
      setSelectedGmailAccountId(null);
    } finally {
      setLoading(false);
    }
  }, [user, selectDefaultAccount]);

  useEffect(() => {
    refreshAccounts();
  }, [refreshAccounts]);

  // Settling period: wait 500ms after accounts first load before enabling URL sync
  // This prevents the initial account selection from triggering URL updates during startup
  useEffect(() => {
    if (gmailAccounts.length > 0 && !hasLoadedAccountsRef.current) {
      hasLoadedAccountsRef.current = true;
      isSettlingRef.current = true;

      const timeout = setTimeout(() => {
        isSettlingRef.current = false;
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [gmailAccounts.length]);

  // Cleanup URL update timeout on unmount
  useEffect(() => {
    return () => {
      if (urlUpdateTimeoutRef.current) {
        clearTimeout(urlUpdateTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Get account ID from localStorage if it exists and is valid.
   *
   * @returns Account ID from localStorage if valid, null otherwise
   */
  const getAccountFromStorage = useCallback((): string | null => {
    if (typeof window === 'undefined') return null;
    const stored = localStorage.getItem('selectedGmailAccountId');
    if (stored && gmailAccounts.some((acc) => acc.id === stored)) {
      return stored;
    }
    return null;
  }, [gmailAccounts]);

  // Effect 1: READ - Sync URL -> State (only when searchParams changes)
  // This reads the account ID from URL and updates state if needed
  useEffect(() => {
    if (gmailAccounts.length === 0) return;

    const urlAccountId = searchParams.get('gmail_account_id');
    const storageAccountId = getAccountFromStorage();
    const defaultAccountId = selectDefaultAccount(gmailAccounts);

    // Priority: URL > Storage > Default
    const targetAccountId =
      urlAccountId && gmailAccounts.some((acc) => acc.id === urlAccountId)
        ? urlAccountId
        : storageAccountId || defaultAccountId;

    // Update selected account if it doesn't match target
    if (targetAccountId && targetAccountId !== selectedGmailAccountId) {
      // Track that this change came from URL/storage/default (not user action)
      changeSourceRef.current = urlAccountId ? 'url' : 'auto';
      setSelectedGmailAccountId(targetAccountId);
    }
  }, [searchParams, gmailAccounts, getAccountFromStorage, selectDefaultAccount]);
  // Note: selectedGmailAccountId intentionally NOT in deps to prevent loops

  // Effect 2: WRITE - Sync State -> URL (debounced, with loop prevention)
  // This updates the URL when account selection changes
  useEffect(() => {
    // Skip if change came from URL (prevents loop)
    if (changeSourceRef.current === 'url') {
      changeSourceRef.current = null;
      return;
    }

    // Skip during settling period
    if (isSettlingRef.current) {
      changeSourceRef.current = null;
      return;
    }

    // Skip if no account selected or no pathname
    if (!selectedGmailAccountId || !pathname) {
      changeSourceRef.current = null;
      return;
    }

    // Skip if URL already has this account ID
    const currentUrlAccountId = searchParams.get('gmail_account_id');
    if (currentUrlAccountId === selectedGmailAccountId) {
      changeSourceRef.current = null;
      return;
    }

    // Debounce URL updates to prevent rapid changes
    if (urlUpdateTimeoutRef.current) {
      clearTimeout(urlUpdateTimeoutRef.current);
    }

    urlUpdateTimeoutRef.current = setTimeout(() => {
      // Double-check settling period hasn't changed
      if (isSettlingRef.current) return;

      try {
        const newPath = buildRouteWithAccount(pathname, selectedGmailAccountId);
        router.replace(newPath);
      } catch (error) {
        logError('Failed to update URL with account ID', {
          accountId: selectedGmailAccountId,
          pathname,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }, 150); // 150ms debounce

    // Reset change source after scheduling the update
    changeSourceRef.current = null;
  }, [selectedGmailAccountId, pathname, searchParams, router]);

  // Sync localStorage with selected account (backup for when URL is not available)
  useEffect(() => {
    if (typeof window !== 'undefined' && selectedGmailAccountId) {
      localStorage.setItem('selectedGmailAccountId', selectedGmailAccountId);
    }
  }, [selectedGmailAccountId]);

  /**
   * Handle manual account selection by the user.
   * Tracks the source as 'user' so the WRITE effect knows to update the URL.
   */
  const handleSetSelectedGmailAccountId = useCallback((accountId: string | null) => {
    // Track that this change came from user action
    changeSourceRef.current = 'user';
    setSelectedGmailAccountId(accountId);
  }, []);

  return (
    <GmailAccountContext.Provider
      value={{
        gmailAccounts,
        selectedGmailAccountId,
        setSelectedGmailAccountId: handleSetSelectedGmailAccountId,
        loading,
        refreshAccounts,
      }}
    >
      {children}
    </GmailAccountContext.Provider>
  );
}

/**
 * Hook to access Gmail account context.
 *
 * Provides access to Gmail accounts, selected account ID, and account management functions.
 * The selected account ID is synchronized with the URL query parameter `gmail_account_id`.
 *
 * @returns Gmail account context with accounts, selected ID, and management functions
 * @throws Error if used outside of GmailAccountProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { gmailAccounts, selectedGmailAccountId, setSelectedGmailAccountId } = useGmailAccount();
 *
 *   return (
 *     <select
 *       value={selectedGmailAccountId || ''}
 *       onChange={(e) => setSelectedGmailAccountId(e.target.value)}
 *     >
 *       {gmailAccounts.map((account) => (
 *         <option key={account.id} value={account.id}>
 *           {account.email}
 *         </option>
 *       ))}
 *     </select>
 *   );
 * }
 * ```
 *
 * @see {@link GmailAccountProvider} - Provider component that must wrap components using this hook
 */
export function useGmailAccount() {
  const context = useContext(GmailAccountContext);
  if (context === undefined) {
    throw new Error('useGmailAccount must be used within a GmailAccountProvider');
  }
  return context;
}
